import { computed, ref, watch, type Ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { isEqual } from 'lodash-es';
import { useQueryClient } from '@tanstack/vue-query';
import {
  useDetails,
  useBooking,
  usePassengers,
} from '@aviata/booking-composable';
import { stateToPayload } from '@aviata/booking-payload-mapper';
import { getBrowserInfo } from '@aviata/browser-info';
import { controllerModel, providerClassLib } from 'shared';
import type { Airflow } from '@aviata/platform-types';
import type { AxiosError } from 'axios';

import { useProductsState } from '@/pages/booking/products/products/model/use-products-state';
import { useProductsPageLoadingState } from '@/pages/booking/products/products/model/use-products-page-loading-state';
import { useSeatMapPricesFromBooking } from '@/pages/booking/products/products/model/use-seat-map-prices-from-booking';
import { usePriceBreakDown } from '@/pages/booking/products/products/model/use-price-break-down';
import { mapPassengers } from '@/pages/booking/products/products/lib';

import { passengersFormLib } from '@/widgets/passengers-form';
import { ancillaryBaggageModel } from '@/widgets/ancillary-baggage';
import { seatMapLib } from '@/widgets/seat-map';
import { contactsFormModel } from '@/widgets/contacts-form';
import { roamingConfig } from '@/widgets/roaming';

import { useFareFamily } from '@/features/fare-family';
import { bookingStateModel } from '@/features/booking-state';
import {
  useSavePassengers,
  savePassengersLib,
} from '@/features/save-passengers';

import { getOfferDetails } from '@/entities/offer';
import { productsLib } from '@/entities/products';

import { api, fetchFareRules } from '@/shared/api';
import { sentryModel } from '@/shared/model';
import {
  amplitudeEventsLib,
  acceleraEventsLib,
  bloomreachLib,
} from '@/shared/lib';
import type { QuickBookingFallbackReason } from '@/shared/lib/amplitude-events/quick-booking';

import type { BookingError } from '../../types';

interface UseQuickBookingOptions {
  offerId: string;
  resultId: string;
  vuelidate: {
    value: {
      $touch(): void;
      $validate(): Promise<boolean>;
      $invalid: boolean;
      $error: boolean;
    };
  };
}

const QB_INIT_ERROR_MODAL = 'quick-booking-init-error-modal';

export function useQuickBooking({
  offerId,
  resultId,
  vuelidate,
}: UseQuickBookingOptions) {
  const router = useRouter();
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const modal = controllerModel.useModal();

  const bookingStore = bookingStateModel.useBookingStore();
  const contactsStore = contactsFormModel.useContactsStore();
  const ancillariesStore = ancillaryBaggageModel.useAncillariesStore();

  // Wipe stale per-offer selections (products, package, ancillaries, seat
  // map, roaming) BEFORE useProductsState below closes over them. The
  // booking store is a Pinia singleton backed by sessionStorage, so without
  // this reset the previous offer's `select_by_default` package and any
  // earlier picks would be re-applied via `applyInitialState` onto a fresh
  // offer. The selectedFareIdsOfferId mismatch is the same discriminator
  // QuickBookingPage.onMounted uses to decide between fresh entry and
  // return-nav from baggage / seat-map.
  const isFreshEntryDifferentOffer =
    bookingStore.selectedFareIdsOfferId !== offerId;
  if (isFreshEntryDifferentOffer) {
    bookingStore.update({
      products: { products: null, state: null },
      packageInfo: null,
      ancillaries: {},
      seatMap: null,
      roaming: null,
    });
  }

  const passengers = usePassengers();
  const { savePassengers } = useSavePassengers();

  const isSubmitting = ref(false);
  const isSavingPassenger = ref(false);
  const submitErrorMessage = ref<string>('');

  const providerClass = computed(
    () => bookingStore.offer?.$meta?.provider_class ?? '',
  );

  const fireFallback = (
    reason: QuickBookingFallbackReason,
    httpStatus?: number,
    errorMessage?: string,
  ) => {
    amplitudeEventsLib.quickBookingFallback({
      offerId,
      reason,
      httpStatus,
      errorMessage,
    });
  };

  const handleNewSearchStart = () => {
    modal.hide(QB_INIT_ERROR_MODAL);
    modal.hide('booking-expired-error-modal');
    router.replace({ name: 'MainPage' });
  };

  /**
   * Show the init-error modal (with "start new search" action that routes back
   * to the main page) when the backend says the offer is expired / gone, even
   * if the error came from a later step than init. Returns true when handled.
   */
  const handlePassengerSaveOfferExpired = (
    err: AxiosError<{ errors: BookingError[] }>,
  ): boolean => {
    const errorCode = err?.response?.data?.errors?.[0]?.code;
    if (errorCode !== 'offer_expired' && errorCode !== 'not_found') {
      return false;
    }
    fireFallback('offer_expired', err?.response?.status, err?.message);
    modal.show(QB_INIT_ERROR_MODAL, {
      title: t('booking.errors.expiredOffer.title'),
      message: t('booking.errors.expiredOffer.description'),
      persistent: true,
    });
    return true;
  };

  /**
   * Gates /airflow/seatmap and /airflow/products fetches behind the initial
   * fare-change call. Flipped to true by QuickBookingPage after
   * `details.changeFare(prepareFarePayload())` settles, so the extras endpoints
   * are queried against the fare context they will be booked under instead of
   * the raw default fare returned by /airflow/details. Defined before
   * `useProductsState` so it can be passed straight into the underlying
   * `useQuery({ enabled })` (which must be created during setup to satisfy
   * vue-query's `inject(QueryClientKey)` lookup).
   */
  const extrasFetchEnabled = ref(false);

  const products = useProductsState({
    offerId,
    packageInfo: bookingStore.state.packageInfo,
    initialState: bookingStore.state.products.state,
    passengers: bookingStore.state.passengers,
    roamingProducts: bookingStore.state.roaming,
    fetchEnabled: extrasFetchEnabled,
  });

  /**
   * useProductsState closes over `bookingStore.state.passengers` at setup
   * (empty `{}` for a fresh QuickBooking entry); bookingStore.update replaces
   * `state.value` wholesale, so the captured reference never reflects the
   * citizenship the user fills in later. These wrappers re-derive the
   * package + product visibility against the LIVE passenger data so the UI
   * (FareCard list / ProductCard list) stops offering insurance to non-KZ
   * pax. Classic ProductsPage already mounts post-passenger-fill, so it is
   * not affected by the stale closure — keeping the override here scopes
   * the fix to QuickBooking.
   */
  const livePassengersForVisibility = computed(
    () => passengers.state.value ?? bookingStore.state.passengers ?? {},
  );
  const qbVisiblePackages = computed(() =>
    productsLib.getVisiblePackages(
      products.packages.value,
      livePassengersForVisibility.value,
    ),
  );
  const qbVisibleProducts = computed(() =>
    productsLib.getVisibleProducts({
      state: products.state.value,
      products: products.products.value,
      passengers: livePassengersForVisibility.value,
    }),
  );

  /**
   * QuickBooking always pre-selects the first available service package on
   * page entry (per product decision — even when the user previously
   * deselected on a different mount). Safe to call multiple times; if no
   * packages are available it no-ops. Returns the package id (or null) so the
   * caller can include it in analytics.
   *
   * Re-filters `products.packages` against the live passenger data instead of
   * trusting useProductsState's cached `visiblePackages`. The cached version
   * closes over `bookingStore.state.passengers` captured at setup time when
   * the store is empty `{}`, and `bookingStore.update()` replaces `state.value`
   * wholesale — so that closure reference goes stale and never sees the
   * citizenship the user fills in later. For a non-KZ passenger that
   * staleness (combined with `[].every() === true` on the empty passenger
   * map) keeps insurance-bearing packages in the candidate list, the package
   * gets auto-selected, and submit sends
   * `passengers_iin: [{iin: "", idx: 0}]` which the backend rejects with
   * "минимальная длина 12". The classic ProductsPage doesn't suffer this
   * because passengers are already filled by the time it mounts, so the
   * fix stays scoped to QuickBooking.
   */
  const preselectFirstPackage = (): string | null => {
    const firstPackage = qbVisiblePackages.value[0];
    if (!firstPackage) {
      return null;
    }
    products.onPackageSelect(firstPackage);
    return firstPackage.id;
  };

  const updateProductsStateAtBookingStore = () => {
    bookingStore.update({
      // Never write `undefined` here — submit's stateToPayload calls
      // Object.values(ancillaries) and would throw silently. After
      // ancillariesStore.clearState() the singleton holds no state, so fall
      // back to {} to keep the booking payload mappable.
      ancillaries: ancillariesStore.ancillaries?.state ?? {},
      products: {
        products: products.products.value,
        state: products.state.value,
      },
      packageInfo: products.packageInfo.value,
      roaming: products.isRoamingSelected.value
        ? products.products.value.filter(
            p => p.service === roamingConfig.ROAMING,
          )
        : [],
    });
  };

  /**
   * Saves passengers without touching contacts. Used right after the passenger
   * bottom-sheet closes so the extras section (products + ancillaries) gets
   * personalized data as soon as the form is filled, mirroring the classic
   * PassengersData → Products transition.
   *
   * Pass `{ silent: true }` for the on-open auto-fire so an `offer_expired`
   * response does not interrupt a page that the user has not yet acted on —
   * the modal will still appear if/when the user actually tries to submit.
   */
  const savePassengersForExtras = async (
    options: { silent?: boolean } = {},
  ): Promise<void> => {
    if (!passengers.state.value || !bookingStore.offer) {
      return;
    }
    const everyFilled = Object.values(passengers.state.value).every(
      pax => Boolean(pax?.firstName) && Boolean(pax?.lastName),
    );
    if (!everyFilled) {
      return;
    }

    bookingStore.update({ passengers: passengers.state.value });

    const currentPayload =
      savePassengersLib.convertLocalPassengersToPassengersPayload(
        passengers.state.value,
        bookingStore.offer?.$meta?.provider_class ?? '',
      );

    if (
      isEqual(
        { offerId, passengers: currentPayload },
        bookingStore.passengersSavePayload,
      )
    ) {
      return;
    }

    ancillariesStore.clearState();
    bookingStore.update({ seatMap: null });
    // Drop the user's prior product/package picks before the refetch — once
    // /airflow/products returns with new IDs the old selection state would
    // be unusable, so make the user opt in again on the fresh data.
    products.resetSelections();
    // Re-apply the QuickBooking default after the wipe — same rule as in
    // `onFareChange`. Without this the first package stays unchecked when a
    // passenger gets re-selected and the user has to opt in manually.
    preselectFirstPackage();
    updateProductsStateAtBookingStore();

    isSavingPassenger.value = true;
    try {
      const res = await savePassengers(offerId, currentPayload);
      bookingStore.setPassengersSaveResponse(
        res as Airflow.Booking.Passenger[],
      );
      // We deliberately do NOT invalidate the ['products', offerId] query.
      // The QB setup useQuery has an active observer the whole time;
      // invalidating triggers an immediate /airflow/products refetch that
      // mints new server-side IDs and invalidates the ones already in
      // `productsModel.items.value` — but `items.value` is a one-shot
      // assignment in `productsModel.init`'s `.then`, it does not track
      // the queryClient cache reactively, so the refetched IDs never reach
      // it. The user's pick freezes a vanished id into state and submit
      // returns "Продукт с таким id не найден". We rely on the single
      // setup-time fetch instead. Seatmap availability is a different
      // story: `checkSeatMapQuery.data.value` IS bound to the cache and
      // can be invalidated safely.
      await queryClient.invalidateQueries({
        queryKey: ['seatMapCheck', offerId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['seatMaps', offerId],
      });
      bookingStore.setPassengersSavePayload({
        offerId,
        passengers: currentPayload,
      });
      bookingStore.updateMedicalInsuranceProducts([]);
      bookingStore.nullifyFlightDelayProducts();
      ancillariesStore.initAncillaries(offerId);
    } catch (err: any) {
      const errorCode = err?.response?.data?.errors?.[0]?.code;
      const isOfferExpired =
        errorCode === 'offer_expired' || errorCode === 'not_found';
      if (isOfferExpired && options.silent) {
        // Auto-fire on page open — don't pre-empt the user with a modal. The
        // explicit submit / sheet-close save will surface the same error if
        // the offer is genuinely gone.
        sentryModel.logBaseError({
          error: err,
          description: 'Quick booking: silent save_passengers offer_expired',
        });
      } else if (isOfferExpired) {
        handlePassengerSaveOfferExpired(err);
      } else {
        const status = err?.response?.status as number | undefined;
        if (status && status >= 400 && status < 500) {
          const message =
            err.response?.data?.errors?.[0]?.message ||
            t('passengersData.saveError.description');
          fireFallback('init_error_4xx', status, message);
          if (options.silent) {
            // Auto-fire on page open / fare-change — don't pre-empt the
            // user with UI. The explicit submit will re-run vuelidate
            // and surface the same issue inline (red border + alert)
            // if it's still present.
            sentryModel.logBaseError({
              error: err,
              description: 'Quick booking: silent save_passengers 4xx',
            });
          } else {
            // Mirror the classic PassengersDataPage flow: surface the
            // backend validation message through the inline alert
            // (driven by `submitErrorMessage`) instead of a blocking
            // modal. The per-passenger red border is handled by the
            // vuelidate state already wired into MultiplePassengerBlock.
            submitErrorMessage.value = message;
          }
        } else {
          sentryModel.captureException(err);
        }
      }
    } finally {
      isSavingPassenger.value = false;
    }
  };

  const details = useDetails({
    offerId,
    resultId,
    fetchDetails: (rid: string, oid: string) =>
      getOfferDetails({ resultId: rid, offerId: oid }),
    fetchFareRules,
    updateFare: (rid: string, oid: string, fareFamilyId: string) =>
      getOfferDetails({ resultId: rid, offerId: oid, fareFamilyId }),
    onInit(loaded: Airflow.Offer.Details) {
      bookingStore.init(loaded);
      bookingStore.update({
        ticketPrice: loaded.offer.price.amount,
        flow: 'quick_booking',
      });

      if (providerClassLib.isFunSun(loaded.offer.$meta?.provider_class ?? '')) {
        fireFallback('funsun_leaked');
        router.replace({ name: 'MainPage' });
      }
    },
    onInitError(error: AxiosError<{ errors: BookingError[] }>) {
      const errorCode = error?.response?.data?.errors?.[0]?.code;
      const isExpired =
        errorCode === 'offer_expired' || errorCode === 'not_found';
      sentryModel.logBaseError({
        error,
        description: 'Quick booking: failed to init page',
      });
      fireFallback(
        isExpired ? 'offer_expired' : 'init_error_5xx',
        error?.response?.status,
        error?.message,
      );
      modal.show(QB_INIT_ERROR_MODAL, {
        title: isExpired
          ? t('booking.errors.expiredOffer.title')
          : t('booking.errors.default.title'),
        message: isExpired
          ? t('booking.errors.expiredOffer.description')
          : t('booking.errors.default.description'),
        persistent: true,
      });
    },
    onFareChange(loaded: Airflow.Offer.Details) {
      ancillariesStore.clearState();
      bookingStore.onUpdateFlights(loaded);
      bookingStore.update({ ticketPrice: loaded.offer.price.amount });
      // Reset cached passenger save payload so the next save preflight is
      // not short-circuited by the isEqual guard.
      bookingStore.setPassengersSavePayload(null as any);
      // Wipe pricelist-bound auxiliaries so they cannot leak stale IDs into
      // the booking payload before the post-save refetch repopulates them.
      bookingStore.updateMedicalInsuranceProducts([]);
      bookingStore.nullifyFlightDelayProducts();
      // Drop the user's prior product/package/seat picks before the refetch.
      // `products.packages.value` survives the reset (it's the catalogue,
      // not the user's pick).
      products.resetSelections();
      preselectFirstPackage();
      bookingStore.update({ seatMap: null });
      queryClient.invalidateQueries({ queryKey: ['seatMaps', offerId] });
      updateProductsStateAtBookingStore();

      // The initial preselect fare-change fires BEFORE extrasFetchEnabled
      // flips — products/seatmap/ancillaries will be fetched once by the
      // gated setup-time init + initPassengersAndSyncExtras pipeline. After
      // that first pass, every subsequent user-initiated fare change goes
      // through this branch and refreshes the fare-bound extras.
      if (!extrasFetchEnabled.value) {
        return;
      }

      // Refresh /airflow/products. Invalidate so the next fetchQuery refetches
      // (staleTime: Infinity would otherwise serve cached data), then re-run
      // products.init to remap items/packages/state to the new server-side IDs.
      queryClient.invalidateQueries({ queryKey: ['products', offerId] });
      products
        .init({ skipDefaultSelection: true })
        .then(() => {
          preselectFirstPackage();
          updateProductsStateAtBookingStore();
        })
        .catch(() => {
          // Errors are reported to Sentry inside getProducts.
        });

      // Re-run the passenger save so /airflow/seatmap and /airflow/ancillaries
      // are refetched against the new fare. savePassengersForExtras is a no-op
      // when passengers aren't filled yet, so it's safe to call unconditionally.
      savePassengersForExtras().catch(() => {
        // Errors surface via modals inside savePassengersForExtras.
      });
    },
    onFareChangeError(error: AxiosError<{ errors: BookingError[] }>) {
      const errorCode = error?.response?.data?.errors?.[0]?.code;
      const isExpired =
        errorCode === 'offer_expired' || errorCode === 'not_found';
      sentryModel.logBaseError({
        error,
        description: 'Quick booking: failed to change fare',
      });
      fireFallback(
        isExpired ? 'offer_expired' : 'init_error_5xx',
        error?.response?.status,
        error?.message,
      );
      modal.show(QB_INIT_ERROR_MODAL, {
        title: isExpired
          ? t('booking.errors.expiredOffer.title')
          : t('booking.errors.default.title'),
        message: isExpired
          ? t('booking.errors.expiredOffer.description')
          : t('booking.errors.default.description'),
        persistent: isExpired,
      });
    },
  });

  const fareFamily = useFareFamily({
    offer: details.offer,
    dict: details.dict,
  });

  const { checkSeatMapQuery, isProductsLoading, isEmptyPage } =
    useProductsPageLoadingState(
      offerId,
      products,
      ancillariesStore,
      extrasFetchEnabled,
    );

  const { seatMapPrice, seatMapOriginalPrice } = useSeatMapPricesFromBooking();

  const priceBreakdown = usePriceBreakDown(
    bookingStore,
    products,
    ancillariesStore,
    seatMapPrice,
    seatMapOriginalPrice,
  );

  const booking = useBooking({
    createOrder(payload: Airflow.Order.Payload.Entity) {
      return api.airflow.createOrder(payload).then(({ data }) => data);
    },
    validateOrder(payload: Airflow.Order.Payload.Entity) {
      return api.airflow.validateOrderData(payload).then(({ data }) => data);
    },
    onDoubleBookingError(error: { message: string; order_id: string }) {
      modal.show('booking-double-booking-modal', {
        message: error.message,
        title: t('contacts.errors.doubleBooking.title'),
        orderId: error.order_id,
      });
      fireFallback('double_booking', undefined, error.message);
      sentryModel.captureException(
        sentryModel.getReadableError(
          'Quick booking error: double booking',
          new Error(error.message),
        ),
      );
    },
    onOfferExpiredError(error: { message: string }) {
      modal.show('booking-expired-error-modal', {
        title: t('contacts.errors.expiredOffer.title'),
        message: error.message,
      });
      fireFallback('offer_expired', undefined, error.message);
      sentryModel.captureException(
        sentryModel.getReadableError(
          'Quick booking error: offer expired',
          new Error(error.message),
        ),
      );
    },
  });

  watch(booking.warnings, newState => {
    if (newState.length > 0) {
      modal.show('booking-warning-modal', {
        title: newState[0].title,
        message: newState[0].message,
      });
    }
  });
  watch(booking.errors, newState => {
    if (newState.length > 0) {
      amplitudeEventsLib.bookingError(newState[0]);
      modal.show('booking-init-error-modal', {
        title: t('contacts.errors.attention'),
        message: newState[0],
      });
      sentryModel.captureException(
        sentryModel.getReadableError(
          'Quick booking error: failed to init order',
          new Error(newState[0]),
        ),
      );
    }
  });

  const goToPaymentPage = (orderId: unknown, showPriceChangeToast: boolean) => {
    router.replace({
      name: 'PaymentPage',
      params: { orderId: String(orderId) },
      query: {
        showPriceChangeToast: String(showPriceChangeToast),
        source: 'quick_booking',
      },
    });
  };

  const goToExistingBooking = (orderId: unknown) => {
    router.replace({
      name: 'PaymentPage',
      params: { orderId: String(orderId) },
    });
  };

  /**
   * Sync passengers + contacts into the booking store right before submit.
   * No `passengers/save` POST: the classic flow doesn't fire one before book
   * either (it ran on the dedicated PassengersDataPage), and in quick booking
   * save has already happened earlier — either on the page-open preflight
   * (`savePassengersForExtras({ silent: true })`) or right after the
   * passenger bottom-sheet closes (`closePassengerSheet`). The booking
   * payload itself carries the full passenger struct, so the server
   * re-validates regardless.
   */
  const persistPassengersAndContacts = (): void => {
    if (!passengers.state.value) {
      throw new Error('Passenger state not initialized');
    }

    bookingStore.update({
      passengers: passengers.state.value,
      contacts: {
        ...contactsStore.state,
        phone: contactsStore.state.phone.slice(2),
      },
    });

    bloomreachLib.update({
      email: contactsStore.state.email,
      isSubscribed: true,
    });
  };

  const submitBooking = async () => {
    const validatingAirlines = Object.keys(
      bookingStore.passengersSchema?.passengers?.schema?.['ADT-0']?.schema
        ?.airline_bonus_cards?.schema ?? {},
    );

    const payload = stateToPayload({
      offerId,
      confirmWarning: booking.isWarningConfirmed.value,
      cancelPrevBooking: booking.shouldCancelPrevBooking.value,
      passengers: mapPassengers(
        bookingStore.state.passengers,
        providerClassLib.isFunSun(bookingStore.offer?.$meta?.provider_class),
        validatingAirlines,
      ),
      contacts: bookingStore.state.contacts,
      airlines: validatingAirlines,
      ancillaries: bookingStore.state.ancillaries,
      products: bookingStore.state.products.state,
      selectedSeats:
        bookingStore.state.seatMap &&
        seatMapLib.preparePayload(bookingStore.state.seatMap),
    });

    payload.products = [
      ...payload.products,
      ...(bookingStore.state.roaming?.map(p => ({ id: p.id })) ?? []),
      ...bookingStore.medicalInsuranceProducts.map(p => p?.payload),
      ...(bookingStore.flightDelayProducts ?? []).map(p => ({ id: p.id })),
    ];

    return booking
      .book(payload)
      .then(data => {
        amplitudeEventsLib.bookingSuccess({
          airlineBonusCards: '',
          orderId: data.id,
          orderNumber: data.number,
          packageInfo: bookingStore.state.packageInfo,
          products: bookingStore.state.products.state,
          seatMap: bookingStore.state.seatMap,
          baggage: bookingStore.state.ancillaries,
          medicalInsurance: bookingStore.medicalInsuranceProducts.length > 0,
          roaming: (bookingStore.state.roaming?.length ?? 0) > 0,
          source: 'quick_booking',
          total: data.total,
          providerClass: bookingStore.offer?.$meta?.provider_class ?? '',
          connectType: bookingStore.offer?.$meta?.connect_type,
          isDomestic: bookingStore.offer?.$meta?.is_domestic,
          searchQuery: bookingStore.offer?.$meta?.search_query,
          flow: 'quick_booking',
        } as any);

        api.ecommerce.sendBrowserInfo(data.id, getBrowserInfo('mobile'));
        bookingStore.update({ roaming: [] });

        const hasPriceChanged = data?.products?.some?.(
          (bookingProduct: Airflow.Product.V2.AirflowBooking) => {
            const offerChanges = bookingProduct?.data?.offer?.price_change;
            const productChanges = bookingProduct?.data?.price_change;
            const checkChanges = offerChanges ?? productChanges;
            return checkChanges?.some(c => Number(c?.difference) > 0);
          },
        );
        goToPaymentPage(data.id, Boolean(hasPriceChanged));
      })
      .catch((error: AxiosError<{ errors: BookingError[] }>) => {
        const status = error?.status;
        if (status && status >= 500 && status < 600) {
          acceleraEventsLib.bookingFailed(error?.message);
        }
        const errorBody = error?.response?.data?.errors?.[0];
        if (
          errorBody?.code === 'offer_expired' ||
          errorBody?.code === 'not_found'
        ) {
          fireFallback('offer_expired', status, error?.message);
          modal.show('booking-expired-error-modal', {
            title: t('contacts.errors.expiredOffer.title'),
            message:
              errorBody.message ?? t('booking.errors.expiredOffer.description'),
            persistent: true,
          });
          return;
        }
        fireFallback('submit_failed', status, error?.message);
      });
  };

  const hasFareSelected = computed(() => {
    const groupCount = Object.keys(fareFamily.fareGroups.value).length;
    if (groupCount === 0) {
      return true; // offer without fare families — nothing to choose
    }
    return Object.keys(fareFamily.selectedFares).length === groupCount;
  });

  const isPassengerFilled = computed(() => {
    const state = passengers.state.value;
    if (!state) {
      return false;
    }
    return Object.values(state).every(
      pax => Boolean(pax?.firstName) && Boolean(pax?.lastName),
    );
  });

  const onSubmit = async () => {
    if (isSubmitting.value) {
      return;
    }
    submitErrorMessage.value = '';

    if (!hasFareSelected.value) {
      submitErrorMessage.value = t('quickBooking.errors.fareRequired');
      fireFallback('validation_failed');
      return;
    }

    if (!isPassengerFilled.value) {
      submitErrorMessage.value = t('quickBooking.errors.passengerRequired');
      fireFallback('validation_failed');
      return;
    }

    vuelidate.value.$touch();
    await vuelidate.value.$validate();
    if (vuelidate.value.$error) {
      fireFallback('validation_failed');
      submitErrorMessage.value = t(
        'passengersData.invalidPassengersData',
        Object.keys(passengers.state.value ?? {}).length,
      );
      return;
    }
    if (!passengers.state.value) {
      submitErrorMessage.value = t('booking.errors.default.title');
      return;
    }
    if (
      passengersFormLib.checkSpecialSymbolsInInitials(passengers.state.value)
    ) {
      modal.show('special-symbols-warning-modal');
      return;
    }

    isSubmitting.value = true;
    try {
      await persistPassengersAndContacts();
      updateProductsStateAtBookingStore();
      await submitBooking();
    } catch {
      // errors surfaced through modals
    } finally {
      isSubmitting.value = false;
    }
  };

  const onWarningConfirm = () => {
    if (booking.warnings.value.length > 1) {
      booking.warnings.value = booking.warnings.value.slice(1);
    } else {
      booking.onWarningConfirm();
      onSubmit().catch(() => undefined);
    }
    modal.hide('booking-warning-modal');
  };

  const onCancelPrevBooking = () => {
    booking.shouldCancelPrevBooking.value = true;
    modal.hide('booking-double-booking-modal');
    onSubmit().catch(() => undefined);
  };

  return {
    details,
    booking,
    bookingStore,
    contactsStore,
    ancillariesStore,
    products: {
      ...products,
      // Override stale closure-bound visibility with live-passenger versions
      // so the UI never offers KZ-only insurance to a non-KZ pax.
      packages: qbVisiblePackages,
      visibleProducts: qbVisibleProducts,
    },
    fareFamily,
    passengers,
    checkSeatMapQuery,
    isProductsLoading,
    isEmptyPage,
    extrasFetchEnabled,
    priceBreakdown,
    isSubmitting,
    isSavingPassenger,
    submitErrorMessage: submitErrorMessage as Ref<string>,
    providerClass,
    hasFareSelected,
    isPassengerFilled,
    onSubmit,
    savePassengersForExtras,
    preselectFirstPackage,
    onWarningConfirm,
    onCancelPrevBooking,
    handleNewSearchStart,
    goToExistingBooking,
    updateProductsStateAtBookingStore,
    modalNames: {
      initError: QB_INIT_ERROR_MODAL,
    },
  };
}
